import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Home, MapPin, Phone, MessageCircle, Mail, Clock, Facebook, Instagram, Youtube, ChevronRight, ExternalLink } from "lucide-react";

const TikTokIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.1a8.16 8.16 0 0 0 4.77 1.53V6.19a4.85 4.85 0 0 1-1.84-.5Z" />
  </svg>
);

const ViberIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.4 0C9.6.1 5.7.4 3.5 2.4 1.9 4 1.3 6.4 1.2 9.3c-.1 3 0 8.5 5.4 10v2.3s-.1.9.5 1.1c.7.2 1.1-.4 1.8-1.1l1.3-1.4c3.7.3 6.5-.4 6.8-.5.7-.2 5-.8 5.6-6.7.7-6-.4-9.9-2.4-11.7C19.6.7 15.9-.1 12.3 0h-.9zm.2 2.1c3-.1 6.3.5 7.8 1.9 1.7 1.5 2.5 4.8 1.9 9.9-.5 5-3.9 5.4-4.5 5.5-.3.1-2.7.7-5.7.5 0 0-2.2 2.6-2.9 3.3-.1.1-.2.1-.3.1-.1 0-.1-.1-.1-.3l.1-3.8c-4.5-1.3-4.3-6-4.2-8.5.1-2.5.5-4.5 1.9-5.9C7 3.5 10.4 3 12 3l-.4-.9zm.4 2.9c-.2 0-.4.2-.4.4s.2.4.4.4c1.7 0 3.1.6 4.2 1.7 1 1.1 1.5 2.5 1.5 4.4 0 .2.2.4.4.4s.4-.2.4-.4c0-2-.6-3.7-1.8-4.9-1.2-1.3-2.8-2-4.7-2zm2 2.2c-.2 0-.4.2-.4.4s.2.4.4.4c1.1 0 2 .3 2.7 1 .7.7 1 1.7 1 2.9 0 .2.2.4.4.4s.4-.2.4-.4c0-1.4-.4-2.5-1.2-3.4-.9-1-2-1.3-3.3-1.3zM7.2 6.7c-.2 0-.3 0-.5.1-.4.2-.7.5-.9.9-.4.8-.5 1.5 0 2.7 1.2 2.9 3 5.3 5.6 6.8.5.3 1.1.5 1.5.7.8.3 1.5.3 2 0 .3-.2.7-.6.9-.9.2-.4.2-.7.1-.9-.2-.3-.7-.5-1.1-.8-.5-.3-1-.4-1.3-.2-.3.1-.4.4-.7.7-.1.1-.2.2-.4.1-.3-.1-.6-.3-.9-.5-1.1-.7-1.9-1.7-2.4-2.7-.1-.1-.1-.3 0-.4.1-.1.3-.2.5-.4.1-.1.2-.2.3-.3.2-.4-.1-.9-.4-1.4-.3-.4-.5-.9-.8-1-.2-.1-.4-.2-.6-.2l.1-.3zm4.3 1.6c-.2 0-.4.2-.4.4s.2.4.4.4c.4 0 .8.1 1 .3.3.3.4.6.4 1.1 0 .2.2.4.4.4s.4-.2.4-.4c0-.7-.2-1.3-.6-1.7-.4-.4-.9-.5-1.6-.5z" />
  </svg>
);

const ContactCard = ({ icon: Icon, iconColor, iconBg, label, value, sub, href, testid, customIcon }) => {
  const inner = (
    <div className="hg-card p-4 sm:p-5 flex items-center gap-4 hover:border-[#FFD600] transition-colors group" data-testid={testid}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {customIcon ? customIcon : <Icon size={22} className={iconColor} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs uppercase tracking-widest font-bold mb-1 ${iconColor}`}>{label}</div>
        <div className="text-sm sm:text-base font-medium truncate">{value}</div>
        {sub && <div className="text-xs text-neutral-400 mt-0.5">{sub}</div>}
      </div>
      <ChevronRight size={20} className="text-neutral-500 group-hover:text-[#FFD600] transition-colors flex-shrink-0" />
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{inner}</a> : inner;
};

const SocialCard = ({ icon: Icon, label, handle, color, bg, href, testid, customIcon }) => {
  // Derive handle from URL if not provided
  const derivedHandle = href ? (() => {
    try {
      const u = new URL(href);
      const path = u.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
      if (!path) return u.hostname.replace(/^www\./, "");
      return path.startsWith("@") ? path : `@${path}`;
    } catch { return handle; }
  })() : handle;
  return (
    <a href={href || "#"} target="_blank" rel="noreferrer" data-testid={testid}
      className={`hg-card p-3 sm:p-4 flex items-center gap-3 hover:border-[#FFD600] transition-colors ${!href ? "opacity-40 pointer-events-none" : ""}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
        {customIcon ? customIcon : <Icon size={20} className={color} />}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold truncate">{label}</div>
        <div className="text-xs text-neutral-400 truncate">{derivedHandle || handle}</div>
      </div>
    </a>
  );
};

export default function Kontakt() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data)).catch(() => setS({}));
  }, []);

  if (!s) return <div className="p-8 text-center text-neutral-400">Duke u ngarkuar...</div>;

  const waNumber = (s.whatsapp_number || "").replace(/\D/g, "");
  const viberNumber = (s.viber_number || s.whatsapp_number || "").replace(/\D/g, "");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-neutral-400 mb-4">
        <Link to="/" className="flex items-center gap-1 hover:text-[#FFD600]"><Home size={14} /> Ballina</Link>
        <span>/</span>
        <span className="text-white">Kontakti</span>
      </div>

      {/* Header + Hero */}
      <div className="grid md:grid-cols-[1fr_auto] items-end gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">Kontakti</h1>
          <div className="h-1 w-16 bg-[#FFD600] mt-3 mb-4" />
          <p className="text-neutral-300 max-w-lg text-sm sm:text-base">
            Për çdo pyetje, porosi apo bashkëpunim,<br />na kontaktoni në mënyrën që ju përshtatet më së miri.
          </p>
        </div>
      </div>

      {/* Contact cards */}
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {s.address && (
          <ContactCard icon={MapPin} iconColor="text-[#FFD600]" iconBg="bg-[#FFD600]/10 border border-[#FFD600]/30"
            label="Adresa" value={s.address.split("\n")[0]} sub={s.address.split("\n").slice(1).join(" ")}
            href={s.google_maps_url || undefined} testid="contact-address" />
        )}
        {s.contact_phone && (
          <ContactCard icon={Phone} iconColor="text-[#FFD600]" iconBg="bg-[#FFD600]/10 border border-[#FFD600]/30"
            label="Telefon" value={s.contact_phone} sub="Na telefononi direkt"
            href={`tel:${s.contact_phone}`} testid="contact-phone" />
        )}
        {s.whatsapp_number && (
          <ContactCard icon={MessageCircle} iconColor="text-emerald-400" iconBg="bg-emerald-500/10 border border-emerald-500/30"
            label="WhatsApp" value={s.whatsapp_number} sub="Shkruani në WhatsApp"
            href={`https://wa.me/${waNumber}`} testid="contact-whatsapp" />
        )}
        {s.viber_number && (
          <ContactCard customIcon={<div className="text-purple-400"><ViberIcon size={22} /></div>}
            iconColor="text-purple-400" iconBg="bg-purple-500/10 border border-purple-500/30"
            label="Viber" value={s.viber_number} sub="Shkruani në Viber"
            href={`viber://chat?number=%2B${viberNumber}`} testid="contact-viber" />
        )}
        {s.email && (
          <ContactCard icon={Mail} iconColor="text-blue-400" iconBg="bg-blue-500/10 border border-blue-500/30"
            label="Email" value={s.email} sub="Përgjigjemi brenda 24h"
            href={`mailto:${s.email}`} testid="contact-email" />
        )}
        {s.working_hours && (
          <ContactCard icon={Clock} iconColor="text-orange-400" iconBg="bg-orange-500/10 border border-orange-500/30"
            label="Orari i punës" value={s.working_hours.split("\n")[0]} sub={s.working_hours.split("\n").slice(1).join(" ")}
            testid="contact-hours" />
        )}
      </div>

      {/* Social */}
      {(s.facebook_url || s.instagram_url || s.tiktok_url || s.youtube_url) && (
        <>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#262626]" />
            <div className="text-xs uppercase tracking-widest text-neutral-400">Na gjeni në rrjetet sociale</div>
            <div className="flex-1 h-px bg-[#262626]" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <SocialCard icon={Facebook} color="text-white" bg="bg-[#1877F2]" label="Facebook" handle="HidroGV" href={s.facebook_url} testid="soc-fb" />
            <SocialCard icon={Instagram} color="text-white" bg="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]" label="Instagram" handle="@hidrogv" href={s.instagram_url} testid="soc-ig" />
            <SocialCard customIcon={<div className="text-white"><TikTokIcon size={20} /></div>} bg="bg-[#131E30] border border-neutral-700" label="TikTok" handle="@hidrogv" href={s.tiktok_url} testid="soc-tt" />
            <SocialCard icon={Youtube} color="text-white" bg="bg-[#FF0000]" label="YouTube" handle="HidroGV" href={s.youtube_url} testid="soc-yt" />
          </div>
        </>
      )}

      {/* Location / Map */}
      {(s.address || s.google_maps_url || s.google_maps_embed) && (
        <div className="hg-card p-4 sm:p-5 grid md:grid-cols-[280px_1fr] gap-4" data-testid="contact-map-block">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#FFD600] mb-3">Lokacioni ynë</div>
            {s.address && (
              <div className="flex gap-2 items-start mb-4">
                <MapPin size={18} className="text-[#FFD600] mt-0.5 flex-shrink-0" />
                <div className="text-sm whitespace-pre-line">{s.address}</div>
              </div>
            )}
            {s.google_maps_url && (
              <>
                <a href={s.google_maps_url} target="_blank" rel="noreferrer"
                  className="hg-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm uppercase tracking-wider w-full sm:w-auto justify-center">
                  <MapPin size={16} /> Shiko në hartë
                </a>
                <a href={s.google_maps_url} target="_blank" rel="noreferrer"
                  className="mt-3 text-sm text-neutral-300 hover:text-[#FFD600] inline-flex items-center gap-1 underline">
                  Hap në Google Maps <ExternalLink size={12} />
                </a>
              </>
            )}
          </div>
          {s.google_maps_embed && (
            <div className="min-h-[280px] rounded overflow-hidden bg-neutral-900 border border-[#1F2B44]">
              <iframe src={s.google_maps_embed} title="Harta" width="100%" height="100%" style={{ minHeight: 280, border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
